import React, { useState, useEffect } from 'react';
import { Menu, X, Moon, Sun, ChevronDown, User, LogOut, LayoutDashboard } from 'lucide-react';

export default function Navigation() {
  const [theme, setTheme] = useState('light');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [postDropdownOpen, setPostDropdownOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (token) {
      setIsLoggedIn(true);
      setUserData(user ? JSON.parse(user) : null);
    }
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    if (savedTheme === 'dark') document.documentElement.classList.add('dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUserData(null);
    window.location.href = '/';
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-b border-gray-200 dark:border-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo — color on light, white version on dark */}
          <button onClick={() => window.location.href = '/'} className="hover:opacity-80 transition-opacity">
            <img src="/logos/logo-color.png" alt="HIVE" className="h-9 w-auto dark:hidden" />
            <img src="/logos/logo-white.png" alt="HIVE" className="h-9 w-auto hidden dark:block" />
          </button>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-4">
            <button onClick={toggleTheme} className="p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
              {theme === 'light' ? <Moon className="w-5 h-5 text-gray-700" /> : <Sun className="w-5 h-5 text-gray-300" />}
            </button>

            {/* Post Dropdown */}
            <div className="relative">
              <button
                onClick={() => setPostDropdownOpen(!postDropdownOpen)}
                onBlur={() => setTimeout(() => setPostDropdownOpen(false), 200)}
                className="px-6 py-2.5 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                Get Started <ChevronDown className="w-4 h-4" />
              </button>
              {postDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
                  <div className="p-2">
                    <button onClick={() => window.location.href = '/post-job'}
                      className="w-full text-left px-4 py-3 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-xl transition-all">
                      <p className="font-semibold text-gray-900 dark:text-white">🙋 I Need Help</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Looking for someone to do a task for you</p>
                    </button>
                    <button onClick={() => window.location.href = '/post-service'}
                      className="w-full text-left px-4 py-3 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-xl transition-all">
                      <p className="font-semibold text-gray-900 dark:text-white">🛠️ I Offer a Service</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">You have a skill and want to earn money</p>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Auth */}
            {isLoggedIn ? (
              <div className="relative group">
                <button className="px-6 py-2.5 bg-white/80 dark:bg-gray-800/80 text-gray-900 dark:text-white rounded-xl font-semibold hover:shadow-lg transition-all border border-gray-200/50 dark:border-gray-700/50 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {userData?.name?.split(' ')[0] || 'User'}
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className="absolute top-full right-0 mt-2 w-48 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <div className="p-2">
                    <button onClick={() => window.location.href = '/profile'}
                      className="w-full text-left px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all">Profile Settings</button>
                    <button onClick={() => window.location.href = '/dashboard'}
                      className="w-full text-left px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all">Dashboard</button>
                    <hr className="border-gray-200 dark:border-gray-700 my-1" />
                    <button onClick={handleLogout}
                      className="w-full text-left px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all font-semibold">Logout</button>
                  </div>
                </div>
              </div>
            ) : (
              <button onClick={() => window.location.href = '/auth'}
                className="px-6 py-2.5 bg-white/80 dark:bg-gray-800/80 text-gray-900 dark:text-white rounded-xl font-semibold hover:shadow-lg transition-all border border-gray-200/50 dark:border-gray-700/50">
                Sign In / Login
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
            {mobileMenuOpen ? <X className="w-6 h-6 text-gray-700 dark:text-gray-300" /> : <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-3 border-t border-gray-200 dark:border-gray-800">
            {isLoggedIn && (
              <div className="flex items-center gap-3 px-3 py-2 mb-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {userData?.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-none">Signed in as</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{userData?.name}</p>
                </div>
              </div>
            )}
            <div className="space-y-1">
              <button onClick={toggleTheme}
                className="w-full flex items-center justify-between px-3 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all">
                <span className="text-sm font-semibold">{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
                {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4 text-yellow-500" />}
              </button>
              <button onClick={() => window.location.href = '/post-job'}
                className="w-full text-left px-3 py-2.5 rounded-xl transition-all hover:bg-gray-100 dark:hover:bg-gray-800">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">🙋 I Need Help</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Looking for someone to do a task</p>
              </button>
              <button onClick={() => window.location.href = '/post-service'}
                className="w-full text-left px-3 py-2.5 rounded-xl transition-all hover:bg-gray-100 dark:hover:bg-gray-800">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">🛠️ I Offer a Service</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Have a skill and want to earn money</p>
              </button>
              {isLoggedIn ? (
                <>
                  <button onClick={() => window.location.href = '/dashboard'}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all text-sm font-semibold">
                    <LayoutDashboard className="w-4 h-4" /> Dashboard
                  </button>
                  <button onClick={() => window.location.href = '/profile'}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all text-sm font-semibold">
                    <User className="w-4 h-4" /> Profile Settings
                  </button>
                  <button onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all text-sm font-semibold">
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </>
              ) : (
                <button onClick={() => window.location.href = '/auth'}
                  className="w-full px-3 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-semibold text-sm hover:bg-gray-800 transition-all">
                  Sign In / Login
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}